/**
 * F37 (Wave 2D) — admin player↔trainer recommendation API client.
 *
 * Covers the three admin surfaces:
 *
 *   Catalog CMS — badge_catalog + review_tag_catalog:
 *     GET    /admin/badge-catalog                 → listBadges
 *     POST   /admin/badge-catalog                 → upsertBadge (by code)
 *     PATCH  /admin/badge-catalog/:id             → updateBadge (by id)
 *     DELETE /admin/badge-catalog/:id             → deleteBadge (soft-deactivate if in use)
 *     GET    /admin/review-tags                   → listTags
 *     POST   /admin/review-tags                   → upsertTag (by code)
 *     PATCH  /admin/review-tags/:code             → updateTag (by code)
 *     DELETE /admin/review-tags/:code             → deleteTag (soft-deactivate if in use)
 *
 *   Review moderation:
 *     GET    /admin/reviews                       → listReviews (status filter, cursor)
 *     GET    /admin/reviews/:id                   → reviewDetail (TRUE identity)
 *     POST   /admin/reviews/:id/moderate          → moderateReview (hide/remove/restore/edit/lock)
 *
 *   Endorsement moderation:
 *     GET    /admin/endorsements                  → listEndorsements (status filter, cursor)
 *     POST   /admin/endorsements/:id/moderate     → moderateEndorsement (remove/restore)
 *
 * Permission gating happens server-side (`recommendation_catalog:read/:write`,
 * `reviews:moderate`, `endorsements:moderate`). The page-level <ProtectedRoute>
 * gates navigation; this client forwards requests and relies on the server to
 * re-enforce RBAC + audit every mutation via the F06 onSend hook.
 *
 * Catalog deletes surface `CATALOG_ENTRY_IN_USE` (409) — the page catches it and
 * re-fetches (the server soft-deactivates the in-use entry instead of deleting).
 */
import type {
  AdminModerateReviewInput,
  AdminPageMeta,
  BadgeCatalogEntry,
  PlayerBadge,
  ReviewStatus,
  ReviewTagEntry,
  TrainerReviewAdmin,
  UpsertBadgeCatalogInput,
  UpsertReviewTagInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

interface CursorEnvelope<T> {
  readonly data: readonly T[];
  readonly nextCursor: string | null;
  readonly pagination?: AdminPageMeta;
}

// ─── Moderation list option + response shapes ───────────────────────────────

export type AdminReviewStatusFilter = ReviewStatus | 'all';
export type AdminEndorsementStatusFilter = 'active' | 'revoked' | 'all';

export interface ListReviewsOpts {
  readonly status?: AdminReviewStatusFilter;
  readonly cursor?: string;
  readonly limit?: number;
  readonly page?: number;
  readonly perPage?: number;
}

export interface ListEndorsementsOpts {
  readonly status?: AdminEndorsementStatusFilter;
  readonly cursor?: string;
  readonly limit?: number;
  readonly page?: number;
  readonly perPage?: number;
}

export interface AdminReviewsListResponse {
  readonly items: readonly TrainerReviewAdmin[];
  readonly nextCursor: string | null;
  readonly pagination?: AdminPageMeta;
}

export interface AdminEndorsementsListResponse {
  readonly items: readonly PlayerBadge[];
  readonly nextCursor: string | null;
  readonly pagination?: AdminPageMeta;
}

/** Endorsement-moderation action (server enum is `remove | restore`). */
export interface ModerateEndorsementInput {
  readonly action: 'remove' | 'restore';
  readonly reason: string;
}

function buildReviewQuery(opts: ListReviewsOpts): string {
  const params: Record<string, string | number | undefined | null> = {
    limit: opts.limit,
    cursor: opts.cursor,
    page: opts.page,
    perPage: opts.perPage,
  };
  if (opts.status && opts.status !== 'all') params.status = opts.status;
  return buildQuery(params);
}

function buildEndorsementQuery(opts: ListEndorsementsOpts): string {
  const params: Record<string, string | number | undefined | null> = {
    limit: opts.limit,
    cursor: opts.cursor,
    page: opts.page,
    perPage: opts.perPage,
  };
  if (opts.status && opts.status !== 'all') params.status = opts.status;
  return buildQuery(params);
}

function isNotFound(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    'status' in err &&
    (err as { status: number }).status === 404
  );
}

// ─── API surface ────────────────────────────────────────────────────────────

export const adminRecommendationApi = {
  // ── Badge catalog ─────────────────────────────────────────────────────────

  /** GET /admin/badge-catalog — full list (incl. inactive). */
  listBadges: async (): Promise<readonly BadgeCatalogEntry[]> => {
    const body =
      await adminFetch<DataEnvelope<readonly BadgeCatalogEntry[]>>('/admin/badge-catalog');
    return body.data;
  },

  /** POST /admin/badge-catalog — create or upsert a badge keyed by `code`. */
  upsertBadge: async (input: UpsertBadgeCatalogInput): Promise<BadgeCatalogEntry> => {
    const body = await adminFetch<DataEnvelope<BadgeCatalogEntry>>('/admin/badge-catalog', {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
    return body.data;
  },

  /** PATCH /admin/badge-catalog/:id — edit a badge by id (code immutable). */
  updateBadge: async (
    id: string,
    patch: Omit<Partial<UpsertBadgeCatalogInput>, 'code'>,
  ): Promise<BadgeCatalogEntry> => {
    const body = await adminFetch<DataEnvelope<BadgeCatalogEntry>>(
      `/admin/badge-catalog/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /**
   * DELETE /admin/badge-catalog/:id — delete, or soft-deactivate when in use.
   * The server returns 409 `CATALOG_ENTRY_IN_USE` when referenced; the caller
   * catches it and re-fetches to reflect the deactivation.
   */
  deleteBadge: async (id: string): Promise<void> => {
    await adminFetch(`/admin/badge-catalog/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // ── Review tag catalog ────────────────────────────────────────────────────

  /** GET /admin/review-tags — full list (incl. inactive). */
  listTags: async (): Promise<readonly ReviewTagEntry[]> => {
    const body = await adminFetch<DataEnvelope<readonly ReviewTagEntry[]>>('/admin/review-tags');
    return body.data;
  },

  /** POST /admin/review-tags — create or upsert a tag keyed by `code`. */
  upsertTag: async (input: UpsertReviewTagInput): Promise<ReviewTagEntry> => {
    const body = await adminFetch<DataEnvelope<ReviewTagEntry>>('/admin/review-tags', {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
    return body.data;
  },

  /** PATCH /admin/review-tags/:code — edit a tag by code (the body omits code). */
  updateTag: async (
    code: string,
    patch: Omit<UpsertReviewTagInput, 'code'>,
  ): Promise<ReviewTagEntry> => {
    const body = await adminFetch<DataEnvelope<ReviewTagEntry>>(
      `/admin/review-tags/${encodeURIComponent(code)}`,
      { method: 'PATCH', body: patch as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** DELETE /admin/review-tags/:code — delete, or soft-deactivate when in use. */
  deleteTag: async (code: string): Promise<void> => {
    await adminFetch(`/admin/review-tags/${encodeURIComponent(code)}`, { method: 'DELETE' });
  },

  // ── Review moderation ─────────────────────────────────────────────────────

  /** GET /admin/reviews — moderation queue (default: flagged). */
  listReviews: async (opts?: ListReviewsOpts): Promise<AdminReviewsListResponse> => {
    const raw = await adminFetch<CursorEnvelope<TrainerReviewAdmin>>(
      `/admin/reviews${buildReviewQuery(opts ?? {})}`,
    );
    return {
      items: raw.data,
      nextCursor: raw.nextCursor,
      ...(raw.pagination !== undefined ? { pagination: raw.pagination } : {}),
    };
  },

  /** GET /admin/reviews/:id — TRUE-identity detail (admin always sees real names). */
  reviewDetail: async (id: string): Promise<TrainerReviewAdmin | null> => {
    try {
      const body = await adminFetch<DataEnvelope<TrainerReviewAdmin>>(
        `/admin/reviews/${encodeURIComponent(id)}`,
      );
      return body.data;
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /** POST /admin/reviews/:id/moderate — hide/remove/restore/edit/lock + reason. */
  moderateReview: async (
    id: string,
    input: AdminModerateReviewInput,
  ): Promise<TrainerReviewAdmin> => {
    const body = await adminFetch<DataEnvelope<TrainerReviewAdmin>>(
      `/admin/reviews/${encodeURIComponent(id)}/moderate`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  // ── Endorsement moderation ────────────────────────────────────────────────

  /** GET /admin/endorsements — reported-endorsement queue. */
  listEndorsements: async (opts?: ListEndorsementsOpts): Promise<AdminEndorsementsListResponse> => {
    const raw = await adminFetch<CursorEnvelope<PlayerBadge>>(
      `/admin/endorsements${buildEndorsementQuery(opts ?? {})}`,
    );
    return {
      items: raw.data,
      nextCursor: raw.nextCursor,
      ...(raw.pagination !== undefined ? { pagination: raw.pagination } : {}),
    };
  },

  /** POST /admin/endorsements/:id/moderate — admin remove/restore an endorsement. */
  moderateEndorsement: async (
    id: string,
    input: ModerateEndorsementInput,
  ): Promise<PlayerBadge> => {
    const body = await adminFetch<DataEnvelope<PlayerBadge>>(
      `/admin/endorsements/${encodeURIComponent(id)}/moderate`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },
};
